import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Github, ExternalLink, Star } from 'lucide-react';
import ScreenHeading from '../../utilities/ScreenHeading/ScreenHeading';
import ScrollService from '../../utilities/ScrollService';
import Animations from '../../utilities/Animations';

const ProjectCard = ({ project }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      /* flex flex-col h-full lets the card fill its carousel slot;
         without this, cards with fewer tech badges are shorter and the row looks ragged */
      className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 rounded-lg shadow-lg hover:shadow-blue-500/20 transition-transform duration-300 hover:-translate-y-2 flex flex-col h-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="border-b border-gray-700 pb-4 mb-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            {project.title}
          </h2>
          {project.featured && (
            <div className="flex items-center text-yellow-400">
              <Star className="w-5 h-5 mr-1 fill-current" />
              <span className="text-sm">Featured</span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {/* key=tech uses the tech name as a stable id — index keys break when the list reorders */}
          {project.technologies.map((tech) => (
            <span
              key={tech}
              className="px-3 py-1 text-sm rounded-full bg-blue-500/20 text-blue-300"
            >
              {tech}
            </span>
          ))}
        </div>
      </div>

      {/* Dark "app window" frame: a browser-chrome bar + soft ring and shadow
          signal "screenshot of a live app" while staying on the card's dark
          palette, so dark UI captures (e.g. Orbit) look intentional instead of
          blending in. Only rendered when the project has an image — otherwise
          the description shows inline below (see the no-image branch). */}
      {project.image ? (
        <div className="relative overflow-hidden rounded-lg bg-slate-950 ring-1 ring-white/10 shadow-lg shadow-black/40">
          {/* window chrome */}
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10 bg-white/5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400/70"></span>
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70"></span>
            <span className="w-2.5 h-2.5 rounded-full bg-green-400/70"></span>
          </div>
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-56 object-contain object-center transition-transform duration-500 group-hover:scale-105"
          />
          {isHovered && (
            <div className="absolute inset-0 bg-black/70 flex items-center justify-center text-center p-4">
              <p className="text-white text-sm">{project.description}</p>
            </div>
          )}
        </div>
      ) : (
        /* No screenshot yet (e.g. in-development apps) — show the description
           inline instead of a broken/empty image frame. */
        <p className="text-slate-300 text-sm leading-relaxed">
          {project.description}
        </p>
      )}

      {/* mt-auto pushes this section to the card bottom regardless of how many tech badges sit above */}
      <div className="border-t border-gray-700 pt-4 mt-auto flex flex-col justify-start mx-auto">
        <div className="flex flex-wrap gap-3">
          {project.github && (
            <a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-4 py-2 rounded-lg bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 transition-colors duration-300"
            >
              <Github className="w-5 h-5 mr-2" />
              Code
            </a>
          )}
          {project.demo && (
            <a
              href={project.demo}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-4 py-2 rounded-lg bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 transition-colors duration-300"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              Demo
            </a>
          )}
          {/* Named public links — used for the Ricoma contributions card, where each
              button points to a publicly visitable site (no login-gated portals) */}
          {project.links &&
            project.links.map((link) => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-4 py-2 rounded-lg bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 transition-colors duration-300"
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                {link.label}
              </a>
            ))}
        </div>
      </div>
    </div>
  );
};

// Hoisted to module scope — static data with no dependency on component state;
// rebuilding it on every render is wasted work and makes it appear new to memoized children
//
// Finished work shown as case studies. Live repo/site links only — no dead
// embeds, no decorative/unverifiable metrics (no "users"/"ratings"). The Ricoma
// entry is professional contribution work consolidated into one card pointing at
// the real public sites (not a personal repo, so no GitHub link / no ownership
// implied). Ricoma is also listed in Work History.
// TODO: add Quantixar case study once the US build ships
//       (sanitized: problem, stack, outcome — no client data).
const projectList = [
  {
    title: 'Ricoma International',
    navLabel: 'Ricoma International',
    // Professional contribution work, not a personal case study — the section
    // subheading reflects that when this card is active.
    subHeading: 'Contributions',
    technologies: ['Vue.js', 'React', 'PHP', 'Laravel', 'MySQL', 'HubSpot', 'Astro', 'Tailwind'],
    image: 'img/ricoma-logo.svg',
    description:
      "Front-end and web work across Ricoma's public commercial site, customer portal, HubSpot marketing landing pages, and e-commerce shop.",
    links: [
      { label: 'ricoma.com', url: 'https://ricoma.com/US' },
      { label: 'Customer Portal', url: 'https://customer.ricoma.com/' },
      { label: 'Landing Pages', url: 'https://info.ricoma.com/' },
      { label: 'Shop', url: 'https://shop.ricoma.com/' },
    ],
  },
  {
    title: 'Orbit',
    navLabel: 'Orbit',
    technologies: [
      'Next.js 14',
      'TypeScript',
      'Prisma',
      'Supabase (PostgreSQL)',
      'Tailwind CSS',
      'Vercel',
    ],
    image: 'img/orbit.png',
    description:
      "A full-stack spaced-repetition app built on the SM-2 scheduling algorithm. It models memory decay per card with forgetting-curve projections and retention thresholds, so each review is scheduled right when you're most likely to forget — with review sessions, an activity heatmap, and shareable decks.",
    demo: 'https://orbit-nine-rouge.vercel.app/',
  },
  {
    title: 'AI Learning Wrapped',
    navLabel: 'AI Learning Wrapped',
    technologies: [
      'TypeScript',
      'Next.js',
      'Google Generative AI',
      'Google Cloud',
    ],
    image: 'img/math_playground.png',
    description:
      "A generative-AI web app that turns a learner's activity into a personalized, shareable \"year in review,\" built with Next.js and the Google Generative AI SDK on Google Cloud.",
    github: 'https://github.com/chrisbolosan/learning-wrapped',
    demo: 'https://learning-wrapped.vercel.app',
  },
  {
    title: 'Quantixar (in development)',
    navLabel: 'Quantixar',
    technologies: ['Swift', 'SwiftUI'],
    description:
      "Native iOS app I'm building toward an App Store release. Swift/SwiftUI.",
  },
];

const Projects = (props) => {
  const [activeIndex, setActiveIndex] = useState(0);

  let fadeInScreenHandler = (screen) => {
    if (screen.fadeInScreen !== props.id) return;
    Animations.animations.fadeInScreen(props.id);
  };
  ScrollService.currentScreenFadeIn.subscribe(fadeInScreenHandler);

  // Guard against activeIndex pointing past the list (e.g. if projectList shrinks).
  const active = projectList[activeIndex] || projectList[0];
  const showToggle = projectList.length > 1;

  return (
    <section id={props.id || 'projects'} className="w-full py-16 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4">
        <ScreenHeading title={'Projects'} subHeading={active.subHeading || 'Case studies'} />

        {/* Labeled toggle — one pill per case study, named up front so a recruiter
            knows what they're looking at. User-driven only (no autoplay), so no one
            loses their place mid-read. Hidden when there's only one study. */}
        {showToggle && (
          <div className="flex justify-center mt-8">
            <div
              role="tablist"
              aria-label="Case studies"
              className="inline-flex flex-wrap justify-center gap-1 rounded-full bg-slate-100/90 backdrop-blur-sm border border-slate-200/80 shadow-sm p-1"
            >
              {projectList.map((project, i) => (
                <button
                  key={project.title}
                  type="button"
                  role="tab"
                  aria-selected={i === activeIndex}
                  onClick={() => setActiveIndex(i)}
                  className={`px-4 py-1.5 text-sm rounded-full transition-colors duration-200 cursor-pointer ${
                    i === activeIndex
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {project.navLabel || project.title}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Active card — re-keyed on the active title so it fades/rises in fresh on
            each toggle. No exit animation / no AnimatePresence keeps the swap snappy
            and sidesteps mode="wait" edge cases. */}
        <div className="mx-auto w-[85vw] sm:w-[420px] lg:w-[480px] mt-8">
          <motion.div
            key={active.title}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            {/* Description lives in the card's hover/tap overlay on the image —
                not repeated below, to avoid duplication. */}
            <ProjectCard project={active} />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
